import { MoreHorizontal, Trash2, X } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import type { DragEvent, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationCenter } from '@shared/components/layout/NotificationCenter';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/components/ui/DropdownMenu';
import { cn } from '@shared/components/ui/cn';
import { ROUTE_I18N_KEYS } from '@shared/lib/route-i18n-keys';
import { type CrmTab, useTabsStore } from '@shared/stores/tabs.store';

interface TabsBarProps {
  onNavigate: (path: string) => void;
  /** AI CIO trigger button, injected by the app layer (module boundaries). */
  aiTrigger?: ReactNode;
}

/** Comfortable tab width from the design (Figma "Tabs / Trigger" = 196px). */
const TAB_MAX_WIDTH = 196;
/** Tabs shrink no further than this; past it, extras move into the "…" menu. */
const TAB_MIN_WIDTH = 120;
/**
 * Layout crossover derived from the design's two tab sizes (196px comfortable →
 * label-left / X-right; ~131px compact → centered). At/above this width tabs use
 * the wide layout; below it they use the compact centered layout. Sits just
 * above the compact size so genuinely wide tabs keep the label-left layout.
 */
const TAB_COMPACT_WIDTH = 140;
/** The gray container's horizontal padding (p-1 both sides). */
const CONTAINER_PADDING = 8;
/** Space reserved inside the pill for the "…" overflow button (w-7 + margin). */
const OVERFLOW_BTN_WIDTH = 32;
/** Row chrome around the pill: px-4 ×2 (32) + gap-3 to the right cluster (12). */
const ROW_CHROME = 44;

export function TabsBar({ onNavigate, aiTrigger }: TabsBarProps) {
  const { t } = useTranslation();
  const { tabs, activeTabId, setActiveTab, closeTab, reorderTabs, closeAllTabs } = useTabsStore();

  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Measure the space available to the gray pill = row inner width minus its
  // chrome and the (bell + AI) cluster. Measuring the right cluster directly
  // avoids the flex-slot rounding that could let the pill overrun and clip.
  const rowRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [availWidth, setAvailWidth] = useState(0);

  // Re-run when the tab count crosses the <2 threshold — below it the row (and
  // its refs) isn't mounted, so a mount-only effect would leave availWidth at 0.
  useLayoutEffect(() => {
    const row = rowRef.current;
    const right = rightRef.current;
    if (!row || !right) return;
    const measure = () => {
      setAvailWidth(Math.max(0, row.clientWidth - ROW_CHROME - right.offsetWidth));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    ro.observe(right);
    return () => {
      ro.disconnect();
    };
  }, [tabs.length]);

  function labelForTab(tab: CrmTab): string {
    // `tab.path` may carry a search string (filters, DEV-4143) — match it
    // against ROUTE_I18N_KEYS by pathname alone, same as AppShell.labelForPath.
    const pathname = tab.path.split('?')[0] ?? tab.path;
    const key = ROUTE_I18N_KEYS[pathname];
    return key ? t(key) : tab.label;
  }

  // ── Fit calculation ─────────────────────────────────────────────────────────
  // Tabs share the available width equally: as wide as 196px when there's room,
  // shrinking (and filling the row) as more are opened. Once the share would
  // drop below the min, the extras move into the "…" menu instead of shrinking
  // further, keeping the active tab visible.
  const usable = Math.max(0, availWidth - CONTAINER_PADDING);
  const activeIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.id === activeTabId),
  );

  let visibleStart = 0;
  let visibleEnd = tabs.length;
  let tabWidth = TAB_MAX_WIDTH;
  if (availWidth > 0) {
    const share = Math.floor(usable / tabs.length);
    if (share >= TAB_MIN_WIDTH) {
      // All tabs fit: equal share, capped at the comfortable 196px.
      tabWidth = Math.min(TAB_MAX_WIDTH, share);
    } else {
      // Overflow: reserve the "…" button, fit as many as we can at the min
      // width, and slide the window so the active tab stays visible.
      const budget = Math.max(TAB_MIN_WIDTH, usable - OVERFLOW_BTN_WIDTH);
      const capacity = Math.max(1, Math.floor(budget / TAB_MIN_WIDTH));
      tabWidth = Math.min(TAB_MAX_WIDTH, Math.floor(budget / capacity));
      const start =
        activeIndex >= capacity ? Math.min(activeIndex - capacity + 1, tabs.length - capacity) : 0;
      visibleStart = start;
      visibleEnd = start + capacity;
    }
  }

  const visibleTabs = tabs.slice(visibleStart, visibleEnd);
  const overflowTabs = tabs.filter((_, i) => i < visibleStart || i >= visibleEnd);
  const hasOverflow = overflowTabs.length > 0;
  const containerWidth =
    visibleTabs.length * tabWidth + (hasOverflow ? OVERFLOW_BTN_WIDTH : 0) + CONTAINER_PADDING;
  // Pick the tab layout by width, matching the design's two states: near the
  // 196px comfortable size the label is left-aligned with the X pinned right
  // (justify-between); once tabs shrink toward the ~131px compact size they use
  // the centered layout (justify-center) with the X right next to the label.
  const compact = tabWidth < TAB_COMPACT_WIDTH;

  function handleTabClick(tab: CrmTab) {
    setActiveTab(tab.id);
    onNavigate(tab.path);
  }

  function handleClose(e: MouseEvent, id: string) {
    e.stopPropagation();
    const path = closeTab(id);
    if (path) onNavigate(path);
  }

  // ── Drag & Drop (indices are global into the tabs array) ─────────────────────

  function onDragStart(e: DragEvent, index: number) {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
  }

  function onDragOver(e: DragEvent, index: number) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }

  function onDrop(e: DragEvent, toIndex: number) {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from !== null && from !== toIndex) {
      reorderTabs(from, toIndex);
    }
    dragIndexRef.current = null;
    setDragOverIndex(null);
  }

  function onDragEnd() {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  }

  // Tab bar is hidden when fewer than 2 pages are open,
  // but the header row always renders to keep buttons in the right position
  if (tabs.length < 2) {
    return (
      // z-50: must outrank the AI CIO panel (z-40, AiCioPanel.tsx) — otherwise,
      // with equal z-index, the later-mounted panel wins the DOM-order
      // tie-break and paints over this whole row, including the AI trigger's
      // hover sub-menu (New Chat/History/Expand), which extends down into the
      // panel's vertical range (top-14) and becomes unclickable while it's open.
      <div className="sticky top-0 z-50 flex h-14 shrink-0 items-center justify-end bg-background px-4 gap-2">
        <NotificationCenter onNavigate={onNavigate} />
        {aiTrigger}
      </div>
    );
  }

  return (
    <div
      ref={rowRef}
      // z-50: see note above — must stay above the AI CIO panel (z-40) so its
      // hover sub-menu remains clickable while the panel is open.
      className="sticky top-0 z-50 flex h-14 shrink-0 items-center justify-between border-b bg-background px-4 gap-3"
    >
      {/* Gray pill — sized to exactly the tabs it holds (plus the "…" button when
          tabs overflow), so the gray only wraps the open tabs and the rest of
          the row stays white. Never wider than the measured available space. */}
      <div
        className="flex shrink-0 items-center overflow-hidden rounded-lg bg-muted p-1"
        style={{ height: 36, width: containerWidth, maxWidth: availWidth || undefined }}
      >
        {visibleTabs.map((tab, i) => {
          const index = visibleStart + i;
          const isActive = tab.id === activeTabId;
          const isDragOver = dragOverIndex === index;
          const label = labelForTab(tab);

          return (
            <div
              key={tab.id}
              role="tab"
              tabIndex={0}
              draggable
              style={{ width: tabWidth }}
              onDragStart={(e) => {
                onDragStart(e, index);
              }}
              onDragOver={(e) => {
                onDragOver(e, index);
              }}
              onDrop={(e) => {
                onDrop(e, index);
              }}
              onDragEnd={onDragEnd}
              onClick={() => {
                handleTabClick(tab);
              }}
              onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') handleTabClick(tab);
              }}
              className={cn(
                'group flex h-full shrink-0 cursor-pointer select-none items-center gap-2 rounded-md px-3 py-1 text-sm font-medium transition-[color,background-color,box-shadow]',
                compact ? 'justify-center' : 'justify-between',
                isActive
                  ? 'bg-background text-foreground shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.06)]'
                  : 'text-muted-foreground hover:text-foreground',
                isDragOver && !isActive && 'ring-1 ring-primary/50',
              )}
            >
              <span className="min-w-0 truncate">{label}</span>
              <button
                className="grid size-4 shrink-0 cursor-pointer place-items-center rounded-sm opacity-70 transition-opacity hover:bg-foreground/10 hover:opacity-100"
                onClick={(e) => {
                  handleClose(e, tab.id);
                }}
                aria-label={t('tabs.close', 'Close {{label}}', { label })}
              >
                <X className="size-4" />
              </button>
            </div>
          );
        })}

        {/* Overflow "…" menu lives INSIDE the pill (per design) and lists only
            the tabs that didn't fit, plus a "Close All". */}
        {hasOverflow && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="ms-0.5 grid h-full w-7 shrink-0 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors hover:bg-background/60 hover:text-foreground"
                aria-label={t('tabs.showHidden', 'Show hidden tabs')}
              >
                <MoreHorizontal className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {overflowTabs.map((tab) => {
                const label = labelForTab(tab);
                return (
                  <DropdownMenuItem
                    key={tab.id}
                    className="flex cursor-pointer items-center justify-between gap-2"
                    onSelect={() => {
                      handleTabClick(tab);
                    }}
                  >
                    <span className="min-w-0 truncate">{label}</span>
                    <button
                      className="grid size-4 shrink-0 cursor-pointer place-items-center rounded-sm opacity-70 transition-opacity hover:bg-foreground/10 hover:opacity-100"
                      aria-label={t('tabs.close', 'Close {{label}}', { label })}
                      // Don't stop pointerdown — it makes Radix select the row
                      // instead of closing. handleClose stops the click reaching onSelect.
                      onClick={(e) => {
                        handleClose(e, tab.id);
                      }}
                    >
                      <X className="size-4" />
                    </button>
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer gap-2 text-destructive focus:text-destructive"
                onSelect={closeAllTabs}
              >
                <Trash2 className="size-4" />
                {t('tabs.closeAll', 'Close All')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Right cluster (outside the pill): Bell + AI. Measured so the pill width
          is exact. */}
      <div ref={rightRef} className="flex shrink-0 items-center gap-2">
        <NotificationCenter onNavigate={onNavigate} />
        {aiTrigger}
      </div>
    </div>
  );
}
